import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { captureProfileGuard } from "../../lib/profileSession";
import { activityReturn } from "../contracts/activityReturn";

/** Activity links select one saved proposal/quotation message. A denied, deleted or
 * missing target never falls through to latest-by-negotiation navigation. */
export function useFocusedActivitySource(input: { sourceId: string | null; sourceKind: string | null; returnToken: string | null;
  routeThreadId: string | null; convo: Awaited<ReturnType<typeof api.getThread>> | null }) {
  const { sourceId, returnToken, routeThreadId, convo } = input;
  const sourceKind = input.sourceKind ?? "project";
  const noun = sourceKind === "project" ? "proposal" : "quotation";
  const [resolved, setResolved] = useState<{ sourceId: string; sourceKind: string; messageId: string; proposalId: string; conversation: typeof convo } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setResolved(null); setError(null);
    if (!sourceId || !routeThreadId || !convo || convo.thread.id !== routeThreadId) return;
    if (sourceKind !== "project" && sourceKind !== "quotation" && sourceKind !== "rfq_items") {
      setError("This exact source is unavailable."); return;
    }
    let current = true;
    const assertCurrent = captureProfileGuard();
    void api.activitySource({ kind: sourceKind, id: sourceId }).then(source => {
      assertCurrent();
      if (!current) return;
      const message = convo.messages.find(candidate => candidate.id === source.messageId);
      const matches = sourceKind === "project"
        ? message?.projectAgreement?.id === sourceId && message.projectAgreement.version === source.proposalVersion && (message.projectAgreement.document?.id ?? null) === source.documentId
        : sourceKind === "quotation" ? message?.quote?.id === sourceId && source.proposalVersion === null && (message.quote.document?.id ?? null) === source.documentId
          : message?.itemQuote?.id === sourceId && source.proposalVersion === null && (message.itemQuote.offer?.document.id ?? null) === source.documentId;
      if (source.threadId !== routeThreadId || source.proposalId !== sourceId || !matches) {
        throw new ApiError(404, "The exact source is unavailable.");
      }
      setResolved({ sourceId, sourceKind, messageId: source.messageId, proposalId: source.proposalId, conversation: convo });
    }).catch(failure => {
      if (current && !(failure instanceof DOMException && failure.name === "AbortError")) setError(failure instanceof ApiError && [401, 403, 404].includes(failure.status)
        ? `This exact ${noun} is unavailable in the selected account or profile.` : `Could not load the exact ${noun}. Please retry.`);
    });
    return () => { current = false; };
  }, [sourceId, sourceKind, noun, routeThreadId, convo]);
  return { requested: !!sourceId, noun, resolved: resolved?.sourceId === sourceId && resolved.sourceKind === sourceKind && resolved.conversation === convo && convo?.thread.id === routeThreadId ? resolved : null, error,
    returnHref: activityReturn(returnToken)?.href ?? null };
}
